/*
 * tt-category-bar.js — progressive enhancement for the category dropdown bar.
 *
 * The bar works with zero JS: each game is a native <details>/<summary> that
 * opens on click/tap (Dawn's <details-disclosure> already closes it on blur).
 * This file layers on the niceties expected of a desktop menu bar:
 *   - hover-to-open on fine-pointer devices (desktop), tap stays native on touch
 *   - opening one game closes the others
 *   - clicking outside the bar, or pressing Escape, closes any open dropdown
 *   - on desktop, the title group is shifted so its centre sits exactly under
 *     the centre of the MIDDLE header-nav item (e.g. "Sell Your Bulk").
 *     Recomputed on resize / font + image load.
 *
 * No card/product markup is built here — see tt-theme.css for styling and
 * sections/tt-category-bar.liquid for the markup. (Frontend rule 1.)
 */
(function () {
  var FINE = window.matchMedia('(hover: hover) and (pointer: fine)');
  var DESKTOP = window.matchMedia('(min-width: 990px)');

  // Top-level <li> items of the desktop header nav, left→right.
  function headerNavItems() {
    var nav = document.querySelector('.header__inline-menu');
    var ul = nav && nav.querySelector('ul');
    if (!ul) return [];
    return Array.prototype.filter.call(ul.children, function (el) {
      return el.tagName === 'LI';
    });
  }

  function itemCenterX(li) {
    var el = li.querySelector('summary, a') || li;
    var r = el.getBoundingClientRect();
    return r.left + r.width / 2;
  }

  // Centre of the middle nav item (odd count) or midpoint of the two central
  // items (even count). Returns null if the nav has no visible items.
  function navMiddleCenterX(items) {
    var n = items.length;
    if (!n || itemCenterX(items[0]) === 0) return null;
    if (n % 2 === 1) return itemCenterX(items[(n - 1) / 2]);
    return (itemCenterX(items[n / 2 - 1]) + itemCenterX(items[n / 2])) / 2;
  }

  // Shift the centred title group so it sits under the middle header-nav item.
  function alignBarToNav(bar) {
    var list = bar.querySelector('.tt-catbar__list');
    var inner = bar.querySelector('.tt-catbar__inner');
    if (!list || !inner) return;

    var items = headerNavItems();
    var target = navMiddleCenterX(items);

    // Only mirror the header for the centred layout while the inline nav shows.
    if (!bar.classList.contains('tt-catbar--align-center') || !DESKTOP.matches || target === null) {
      list.style.transform = '';
      return;
    }

    var innerRect = inner.getBoundingClientRect();
    var delta = Math.round(target - (innerRect.left + innerRect.width / 2));
    list.style.transform = delta ? 'translateX(' + delta + 'px)' : '';
  }

  function alignAll() {
    document.querySelectorAll('.tt-catbar').forEach(alignBarToNav);
  }

  // Publish each category bar's real rendered height as --tt-catbar-height on
  // its own section. The sticky-reveal CSS slides the bar out of view with
  // `top: calc(-1 * var(--tt-catbar-height))` so it animates on the same
  // property/thread as Dawn's header (which transitions `top`) and stays locked
  // to it — a GPU transform would race ahead and open a gap on scroll-up. Using
  // the measured height (incl. wrapped rows on mobile) makes the bar emerge from
  // under the header in exact step rather than over-shooting.
  function publishCatbarHeight(section) {
    var h = section.offsetHeight;
    if (h) section.style.setProperty('--tt-catbar-height', h + 'px');
  }

  function publishCatbarHeights() {
    document.querySelectorAll('.tt-catbar-section').forEach(publishCatbarHeight);
  }

  // Height also changes when rows wrap/unwrap without a window resize event
  // (e.g. fonts/images settling) — keep it current with a ResizeObserver.
  function observeCatbarHeights() {
    if (!('ResizeObserver' in window)) return;
    document.querySelectorAll('.tt-catbar-section').forEach(function (section) {
      if (section.ttHeightObserved) return;
      section.ttHeightObserved = true;
      new ResizeObserver(function () { publishCatbarHeight(section); }).observe(section);
    });
  }

  var alignScheduled = false;
  function scheduleAlign() {
    if (alignScheduled) return;
    alignScheduled = true;
    window.requestAnimationFrame(function () {
      alignScheduled = false;
      alignAll();
      publishCatbarHeights();
    });
  }

  function closeAllExcept(bar, keep) {
    bar.querySelectorAll('details[open]').forEach(function (d) {
      if (d !== keep) d.open = false;
    });
  }

  function initBar(bar) {
    if (bar.dataset.ttCatbarReady) return;
    bar.dataset.ttCatbarReady = 'true';

    bar.querySelectorAll('.tt-catbar__item').forEach(function (item) {
      var details = item.querySelector('details');
      if (!details) return;
      var closeTimer;

      item.addEventListener('mouseenter', function () {
        if (!FINE.matches) return;
        window.clearTimeout(closeTimer);
        closeAllExcept(bar, details);
        details.open = true;
      });

      item.addEventListener('mouseleave', function () {
        if (!FINE.matches) return;
        closeTimer = window.setTimeout(function () {
          details.open = false;
        }, 120);
      });

      // Tap / keyboard open: make it exclusive too.
      details.addEventListener('toggle', function () {
        if (details.open) closeAllExcept(bar, details);
      });
    });

    var onDocClick = function (event) {
      /* Editor section re-renders replace the bar; let the orphaned
         listener remove itself instead of accreting per re-init. */
      if (!bar.isConnected) { document.removeEventListener('click', onDocClick); return; }
      if (bar.contains(event.target)) return;
      closeAllExcept(bar, null);
    };
    document.addEventListener('click', onDocClick);

    bar.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeAllExcept(bar, null);
    });
  }

  // On phones/tablets the standalone strip is hidden (see tt-theme.css) and the
  // games are shown inside the hamburger menu instead. Clone the games list into
  // the menu drawer, above the Home/About Us links. Single source of truth: the
  // Category-bar section still owns the data; this is just a mirror. CSS restyles
  // the clone as full-width drawer rows with inline (accordion) dropdowns.
  function mirrorIntoDrawer() {
    var bar = document.querySelector('.tt-catbar');
    var list = bar && bar.querySelector('.tt-catbar__list');
    var nav = document.querySelector('#menu-drawer .menu-drawer__navigation');
    if (!list || !nav) return;

    var existing = nav.querySelector('.tt-catbar-drawer');
    if (existing) existing.remove();

    var wrap = document.createElement('div');
    wrap.className = 'tt-catbar-drawer';
    var clone = list.cloneNode(true);
    clone.style.transform = ''; // drop any desktop centring transform

    // Dawn's <details-disclosure> closes on blur — wrong inside the drawer. Keep
    // plain native <details> so a tap just expands/collapses the game inline.
    clone.querySelectorAll('details-disclosure').forEach(function (dd) {
      var d = dd.querySelector('details');
      if (d) dd.replaceWith(d);
    });

    // Keep aria-expanded in sync and only one game open at a time.
    clone.querySelectorAll('details').forEach(function (d) {
      var s = d.querySelector('summary');
      d.addEventListener('toggle', function () {
        if (s) s.setAttribute('aria-expanded', d.open ? 'true' : 'false');
        if (d.open) {
          clone.querySelectorAll('details[open]').forEach(function (o) { if (o !== d) o.open = false; });
        }
      });
    });

    wrap.appendChild(clone);
    nav.appendChild(wrap); // games sit BELOW the Home / About Us nav items
  }

  function init() {
    document.querySelectorAll('.tt-catbar').forEach(initBar);
    mirrorIntoDrawer();
    publishCatbarHeights();
    observeCatbarHeights();
    scheduleAlign();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Widths shift as fonts and the logo image finish loading — realign then.
  window.addEventListener('load', scheduleAlign);
  window.addEventListener('resize', scheduleAlign);
  if (DESKTOP.addEventListener) DESKTOP.addEventListener('change', scheduleAlign);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleAlign);

  // Re-init / realign when a header or this section is re-rendered in the editor.
  document.addEventListener('shopify:section:load', init);
})();
